import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { DeviationData } from "../Deviation/DeviationData";
import CollectionLoader, { CollectionLoadedListener } from "../General/CollectionLoader";
import { FirebaseData } from "../General/FirebaseData";
import { FirebaseInfoField } from "../General/FirebaseInfoField";
import { FirebaseFile } from "../General/FirebaseFile";
import { LocalData } from "../General/LocalData";
import { Role } from "../User/Role";
import { UserData } from "../User/UserData";
import BookingData from "./Booking/BookingData";
import { IncomeOverviewData } from "./Income/IncomeOverviewData";
import { MemberData } from "./MemberData";
import { t } from "i18next";
import { DataCreator } from "../../../utils/dataCreator";
import { RoleTitle } from "./RoleTitle";
import { Utilities } from "../../../utils/utilities";
import { UnitData } from "./Units/UnitData";
import BookingUnitData from "./Booking/BookingUnitData";
import { AuthenticationTokenData } from "../General/AuthenticationTokenData";
import { CompanyData } from "../Company/CompanyData";
import { PropertyCollectionData } from "./Collections/PropertyCollectionData";
import { CompanyActionValidatorData } from "../Company/Settings/CompanyActionValidator";
import { DataTypeData } from "../Company/DataTypeData";
import { PropertyInfoFields } from "./PropertyInfoFields";
import {
  CollectionReference,
  DocumentData,
  FirestoreError,
  GeoPoint,
  arrayUnion,
  collection,
  doc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { FirebaseInfoFieldVisibility } from "../General/DataObjectHelpers/Property/InfoFields/InfoFieldTypes";
import { AppModuleID, AppModules } from "./AppModules";
import { PropertyFileData } from "../General/PropertyFileData";

export class PropertyData extends FirebaseData {
  name: string = "";
  propertyHeader?: FirebaseFile;
  smallPropertyHeader?: FirebaseFile;
  companies: CompanyData[] = [];
  contractors: ContractorGroupData[] = [];

  roleTitles: { [index: number]: RoleTitle } = {
    0: new RoleTitle(0, "tenant", t("main.tenant"), t("main.tenants")),
    1: new RoleTitle(1, "landlord", t("main.landlord"), t("main.landlords")),
  };

  location?: FirebaseInfoField<GeoPoint>;
  address?: FirebaseInfoField<string>;
  visitorAddress?: FirebaseInfoField<string>;
  postalAddress?: FirebaseInfoField<string>;
  city?: FirebaseInfoField<string>;

  municipality?: FirebaseInfoField<string>;
  municipalityNumber?: FirebaseInfoField<string>;

  gnr?: FirebaseInfoField<string>;
  bnr?: FirebaseInfoField<string>;
  snr?: FirebaseInfoField<string>;
  floors?: FirebaseInfoField<number[]>;
  totalParkingSpots?: FirebaseInfoField<number>;
  constructionYear?: FirebaseInfoField<string>;

  organizationNumber?: FirebaseInfoField<string>;
  organizationName?: FirebaseInfoField<string>;

  plotArea?: FirebaseInfoField<number>;
  totalArea?: FirebaseInfoField<number>;
  usageArea?: FirebaseInfoField<number>;
  builtArea?: FirebaseInfoField<number>;
  netArea?: FirebaseInfoField<number>;
  primaryArea?: FirebaseInfoField<number>;
  secondaryArea?: FirebaseInfoField<number>;
  rentableArea?: FirebaseInfoField<number>;
  jointArea?: FirebaseInfoField<number>;
  ownedArea?: FirebaseInfoField<number>;
  rentedArea?: FirebaseInfoField<number>;
  heatedArea?: FirebaseInfoField<number>;

  buildingOwner?: FirebaseInfoField<string>;
  architects?: FirebaseInfoField<string>;
  managementCompanies?: FirebaseInfoField<string>;
  insuranceCompanies?: FirebaseInfoField<string>;
  fireSafetyResponsibles?: FirebaseInfoField<UserData[]>;

  riskClass?: FirebaseInfoField<string>;
  fireClass?: FirebaseInfoField<string>;
  energyScore?: FirebaseInfoField<string>;
  heatingScore?: FirebaseInfoField<string>;
  protectionClass?: FirebaseInfoField<string>;
  maxPeopleAllowed?: FirebaseInfoField<number>;

  jointCost?: FirebaseInfoField<number>;
  totalJointCost?: FirebaseInfoField<number>;
  marketValue?: FirebaseInfoField<number>;
  balanceSheetValue?: FirebaseInfoField<number>;
  grossYield?: FirebaseInfoField<number>;
  purchaseDate?: FirebaseInfoField<Date>;
  purchaseSum?: FirebaseInfoField<number>;
  initialLoan?: FirebaseInfoField<number>;
  remainingLoan?: FirebaseInfoField<number>;

  note?: FirebaseInfoField<string>;

  infoFieldsLoaded: boolean = false;
  infoFieldsLoading: boolean = false;
  infoFieldsLoadedListeners: ((error?: FirestoreError) => void)[] = [];

  units: UnitData[] = [];
  unitsLoaded: boolean = false;
  unitsLoading: boolean = false;
  unitsLoadedListeners: ((error?: FirestoreError) => void)[] = [];

  deviations: CollectionLoader<DeviationData>;
  members: CollectionLoader<MemberData>;
  bookingUnits: CollectionLoader<BookingUnitData>;
  bookings: CollectionLoader<BookingData>;
  collections: CollectionLoader<PropertyCollectionData>;
  files: CollectionLoader<PropertyFileData>;
  dataTypes: CollectionLoader<DataTypeData>;
  actionValidator: CompanyActionValidatorData;

  incomeOverview: IncomeOverviewData = new IncomeOverviewData(this);

  modules: { [key: string]: { active: boolean } } = {};

  archived: boolean = false;

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.deviations = new CollectionLoader("deviations", this, DeviationData);
    this.members = new CollectionLoader("members", this, MemberData);
    this.bookingUnits = new CollectionLoader("bookingUnits", this, BookingUnitData);
    this.bookings = new CollectionLoader("bookings", this, BookingData);
    this.collections = new CollectionLoader("collections", this, PropertyCollectionData);
    this.files = new CollectionLoader("files", this, PropertyFileData);
    this.dataTypes = new CollectionLoader("dataTypes", this, DataTypeData);
    this.actionValidator = new CompanyActionValidatorData("actionValidator", "settings", this);
  }

  archive() {
    const newArchiveStatus = !this.archived;
    return this.saveUpdate({ archived: newArchiveStatus }).then(() => {
      this.archived = newArchiveStatus;
    });
  }

  setupInfoFields(data: DocumentData) {
    if (typeof data.fields === "object") {
      PropertyInfoFields.loadFields(this, data.fields);
    }
  }

  setup(data: any) {
    if (typeof data !== "object") return;
    if (typeof data.name === "string") {
      this.name = data.name;
    }
    // Files
    if (typeof data.headerImage === "object") {
      this.propertyHeader = FirebaseFile.initialize("headerImage", data.headerImage);
    }
    if (typeof data.smallHeaderImage === "object") {
      this.smallPropertyHeader = FirebaseFile.initialize("smallHeaderImage", data.smallHeaderImage);
    }

    if (Array.isArray(data.contractorGroups)) {
      this.contractors = [];
      for (const contractorID of data.contractorGroups) {
        if (typeof contractorID === "string") this.contractors.push(LocalData.contractorGroups.addData(contractorID));
      }
    }
    if (Array.isArray(data.companies)) {
      this.companies = [];
      for (const companyID of data.companies) {
        if (typeof companyID === "string") {
          const company = LocalData.companies.addData(companyID);
          // MOTIVATION:
          // if user doesn't have any company id in companies list, then company data loading would be skipped
          // (second place where loading data performing is LocalData.ts#L134), that's why we should load company data from here, but we decided to add condition to avoid extra db queries

          // load company data only if user's company list is empty and on the other hand user have access to properties (for instance if user has a contractor role)
          if (LocalData.currentUser.companies.length === 0 && LocalData.currentUser.properties.length > 0) {
            company.load();
          }

          this.companies.push(company);
        }
      }
    }

    // RoleTitles
    if (typeof data.roleTitles === "object") {
      for (let roleId in data.roleTitles) {
        let roleNrId = Utilities.convertToNumber(roleId);
        this.roleTitles[roleNrId] = RoleTitle.getRoleTitlesFromData(roleNrId, data.roleTitles[roleId]);
      }
    }

    // MODULES
    if (typeof data.modules === "object") {
      for (let moduleId in data.modules) {
        let moduleObject = data.modules[moduleId];
        this.modules[moduleId] = {
          active: typeof moduleObject.active === "boolean" ? moduleObject.active : true,
        };
      }
    }

    this.archived = typeof data.archived === "boolean" ? data.archived : false;

    if (typeof data.fields === "object") {
      PropertyInfoFields.loadFields(this, data.fields);
    }
    if (data) {
      super.setup(data);
    }
  }

  getInfoFieldRoleArray(): FirebaseInfoFieldVisibility[] {
    const roleArray: FirebaseInfoFieldVisibility[] = [];

    const member = this.members.getData(LocalData.currentUser.id);
    if (member?.hasLandlord()) roleArray.push(member.isAdmin ? "landlordAdmin" : "landlord");
    if (member?.hasTenant()) roleArray.push(member.isAdmin ? "tenantAdmin" : "tenant");
    if (member?.hasEntrepreneur()) roleArray.push("entrepreneur");
    return roleArray;
  }

  loadInfoFields(completion: () => void) {
    const roleArray = this.getInfoFieldRoleArray();
    const db = getFirestore();
    const reportsRef = collection(db, "properties", this.id, "infoFields");

    this.infoFieldsLoadedListeners.push(completion);
    if (!this.infoFieldsLoading) {
      this.infoFieldsLoading = true;
      if (roleArray.length > 0) {
        const filteredQuery = query(reportsRef, where("visibility", "array-contains-any", roleArray));
        getDocs(filteredQuery)
          .then((snapshot) => {
            for (let i = 0; i < snapshot.docs.length; i++) {
              const data = snapshot.docs[i].data();
              this.setupInfoFields(data);
            }

            this.infoFieldsLoaded = true;
            this.infoFieldsLoading = false;
            this.infoFieldsLoadedListeners.forEach((listener) => {
              listener(undefined);
            });
            return;
          })
          .catch((error) => {
            console.log(error);
            this.infoFieldsLoaded = true;
            this.infoFieldsLoading = false;
            this.infoFieldsLoadedListeners.forEach((listener) => {
              listener(error);
            });
            return;
          });
      } else {
        this.infoFieldsLoaded = true;
        this.infoFieldsLoading = false;
        this.infoFieldsLoadedListeners.forEach((listener) => {
          listener(undefined);
        });
      }
    }
  }

  loadUnits(completion: () => void) {
    console.log("LOADING UNITS");
    this.unitsLoadedListeners.push(() => completion());
    if (!this.unitsLoading) {
      const loadingCompleted = (error: FirestoreError | undefined) => {
        this.unitsLoaded = true;
        this.unitsLoading = false;
        this.unitsLoadedListeners.forEach((listener) => {
          listener(error);
        });
        this.unitsLoadedListeners = [];
      };

      const roles = this.checkForUserRoles(LocalData.currentUser);
      if (
        roles?.includes(Role.LANDLORD) ||
        roles?.includes(Role.ENTREPRENEUR) ||
        AuthenticationTokenData.token?.claims.properties.find((p) => p.data === this)?.units === "all"
      ) {
        this.unitsLoading = true;
        const db = getFirestore();
        const units = collection(db, "properties", this.id, "units");
        getDocs(units)
          .then((snapshot) => {
            const docs = snapshot.docs.filter((doc) => !doc.data().deletedBy);

            for (const doc of docs) {
              const data = doc.data();
              const unit = this.getUnitWithId(doc.id);
              unit.setup(data);
            }
            loadingCompleted(undefined);
          })
          .catch((error) => {
            console.error("Error loading units " + error.message);
            loadingCompleted(error);
          });
      } else {
        let unitsToLoad: UnitData[] = [];
        let unitsLoaded = 0;

        if (LocalData.currentUser.isExternal) {
          const externalUnits = AuthenticationTokenData.token?.claims.properties.find((p) => p.data === this)?.units;
          if (Array.isArray(externalUnits)) {
            unitsToLoad = externalUnits.filter((unit) => !unit.deleted);
          }
        } else {
          unitsToLoad = this.members.getData(LocalData.currentUser.id)?.units.filter((unit) => !unit.deleted) ?? [];
        }
        if (unitsToLoad.length === 0) {
          loadingCompleted(undefined);
        }
        for (const unit of unitsToLoad) {
          const thisUnit = this.getUnitWithId(unit.id);
          thisUnit.loadWithCompletionHandler(() => {
            unitsLoaded++;
            if (unitsLoaded >= unitsToLoad.length) {
              loadingCompleted(undefined);
            }
          });
        }
      }
    }
  }

  getRentableArea(): number {
    return this.rentableArea?.value ?? this.totalArea?.value ?? 0;
  }

  /**
   * @deprecated Use useApiUnits instead to load units and calculate the rent
   */
  getTotalAnnualRent() {
    if (Array.isArray(this.units)) {
      let totalAnnualRent = 0;
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (!unit.archived) {
          let annualRent = 0;
          const incomeData = unit.incomeData?.value;
          if (incomeData) {
            annualRent = incomeData.sumOfIncomes(12);
          }
          totalAnnualRent += annualRent;
        }
      }
      return totalAnnualRent;
    } else {
      return 0;
    }
  }

  /**
   * @deprecated Use useApiUnits instead to load units and calculate the rent
   */
  private getTotalAnnualAreaRent() {
    if (Array.isArray(this.units)) {
      let totalAnnualRent = 0;
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (!unit.archived) {
          let areaRent = unit.areaRent();
          totalAnnualRent += areaRent;
        }
      }
      return totalAnnualRent;
    } else {
      return 0;
    }
  }

  /**
   * @deprecated Use useApiUnits instead to load units and calculate the rent
   */
  private getTotalAnnualParkingRent() {
    if (Array.isArray(this.units)) {
      let totalAnnualRent = 0;
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (!unit.archived) {
          let areaRent = unit.parkingRent();
          totalAnnualRent += areaRent;
        }
      }
      return totalAnnualRent;
    } else {
      return 0;
    }
  }

  /**
   * @deprecated Use useApiUnits instead to load units and calculate the rent
   */
  private getTotalAnnualOtherRent() {
    if (Array.isArray(this.units)) {
      let totalAnnualRent = 0;
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (!unit.archived) {
          let areaRent = unit.otherRent();
          totalAnnualRent += areaRent;
        }
      }
      return totalAnnualRent;
    } else {
      return 0;
    }
  }

  /**
   * @deprecated Use useApiUnits instead to load units and calculate the total rented area
   */
  getRentedArea(): number {
    if (this.rentedArea?.value) return this.rentedArea.value;
    let totalRentedArea = 0;
    if (Array.isArray(this.units)) {
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (unit.area?.value && !unit.archived) {
          totalRentedArea += unit.area.value;
        }
      }
    }

    return totalRentedArea;
  }

  getCalculatedGrossYield(): number {
    const totalIncome = this.getTotalAnnualRent();
    if (typeof this.marketValue?.value === "number" && totalIncome > 0) {
      return totalIncome / this.marketValue.value;
    }
    return 0;
  }
  getCalculatedMarketValue(): number {
    const totalIncome = this.getTotalAnnualRent();
    if (typeof this.grossYield?.value === "number" && totalIncome > 0) {
      return totalIncome / this.grossYield.value;
    }
    return 0;
  }

  getPricePerArea() {
    let annualAreaRent = this.getTotalAnnualAreaRent();
    let area: number = this.getRentedArea();
    if (area && annualAreaRent) {
      return annualAreaRent / area;
    }
    return 0;
  }

  /**
   * @deprecated Use useApiUnits to load units and calculate the total parking spots
   */
  availableParkingSpots() {
    if (Array.isArray(this.units)) {
      if (this.totalParkingSpots) {
        let totalParkingSpots = this.totalParkingSpots.value ?? 0;
        for (let i = 0; i < this.units.length; i++) {
          const unit = this.units[i];
          if (!unit.archived) {
            if (unit.parkingSpots !== undefined && unit.parkingSpots?.value) {
              totalParkingSpots -= unit.parkingSpots.value;
            } else if (unit.incomeData?.value) {
              totalParkingSpots -= unit.incomeData.value.sumOfParkingSpots();
            }
          }
        }
        return totalParkingSpots;
      } else {
        return 0;
      }
    } else {
      return this.totalParkingSpots;
    }
  }

  /**
   * @deprecated Use useApiUnits to load units and calculate the total remaining contract sum
   */
  getTotalRemainingContractSum() {
    if (Array.isArray(this.units)) {
      let totalRemainingContractSum = 0;
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (!unit.archived) {
          totalRemainingContractSum += unit.getRemainingContractSum();
        }
      }
      return totalRemainingContractSum;
    } else {
      return 0;
    }
  }

  /**
   * @deprecated Use useApiUnits to load units and calculate the total contract value
   */
  getTotalContractValue() {
    let totalContractValue = 0;
    for (const unit of this.units) {
      if (!unit.archived) {
        totalContractValue += unit.getContractValue();
      }
    }
    return totalContractValue;
  }

  getLoanToValue(): number {
    if (this.marketValue?.value && this.remainingLoan?.value) {
      return this.remainingLoan.value / this.marketValue.value;
    }
    return 0;
  }

  /**
   * @deprecated Use useApiUnits to load units and calculate the total annual rent
   */
  getVatKey(): number {
    const totalArea = this.getRentedArea();
    const vatApplicableUnits = this.units.filter((unit) => unit.vatApplicable?.value);
    const vatApplicableArea = vatApplicableUnits.reduce((acc, unit) => acc + (unit.area?.value ?? 0), 0);
    if (totalArea > 0) {
      return vatApplicableArea / totalArea;
    }
    return 0;
  }

  loadDeviations(completion: (data: DeviationData[]) => void) {
    this.deviations.loadWithFilter(
      (ref) => filterRef(ref),
      (deviations) => {
        completion(deviations);
      }
    );

    const that = this;
    function filterRef(deviationsRef: CollectionReference<DocumentData>) {
      query(deviationsRef, orderBy("reportDate", "desc"));
      const member = that.members.getData(LocalData.currentUser.id);
      if (member) {
        if (member.hasLandlord() || member.isOperationManager) {
          return deviationsRef;
        } else {
          if (member.hasTenant()) {
            const unitIDs = member.units.map((unit) => unit.id);
            if (unitIDs.length > 0) {
              const tenantRef = deviationsRef;
              query(tenantRef, where("visibility.tenants", "==", true));
              query(tenantRef, where("unitIDs", "array-contains-any", unitIDs));
              return tenantRef;
            }
          }
        }
        if (member.hasEntrepreneur()) {
          const contractorIds: string[] = LocalData.currentUser.contractorGroups
            .filter((contractorGroup) => contractorGroup.properties.includes(that))
            .map((group) => group.id);

          if (contractorIds.length > 0) {
            const contractorRef = deviationsRef;
            query(contractorRef, where("contractorGroups", "array-contains-any", contractorIds));
            return contractorRef;
          }
        }
      }

      return deviationsRef;
    }
  }

  /** @deprecated use addMemberAsync */
  addMember(user: UserData, role: number, units: UnitData[], admin: boolean, operationManager: boolean, completion: (error?: Error) => void) {
    const db = getFirestore();
    const memberRef = doc(db, "properties", this.id, "members", user.id);

    let memberData: { [key: string]: any } = {
      admin,
      operationManager,
      updatedBy: DataCreator.getUpdatedBy(),
    };
    if (units.length > 0) {
      memberData["units"] = arrayUnion(...units.map((unit) => unit.id));
    }
    if (role === 1) {
      memberData["roles"] = arrayUnion(role);
    }

    setDoc(memberRef, memberData, { merge: true })
      .then(() => {
        const member = this.members.addData(user.id);
        member.units.push(...units);
        member.setup(memberData);
        if (!user.properties.includes(this)) user.properties.push(this);
        completion();
      })
      .catch((error) => {
        completion(error);
      });
  }

  async addMemberAsync(user: UserData, role: number, units: UnitData[], admin: boolean, operationManager: boolean) {
    const db = getFirestore();
    const memberRef = doc(db, "properties", this.id, "members", user.id);

    const memberData: { [key: string]: any } = {
      admin,
      operationManager,
      updatedBy: DataCreator.getUpdatedBy(),
    };
    if (units.length > 0) {
      memberData["units"] = arrayUnion(...units.map((unit) => unit.id));
    }
    if (role === 1) {
      memberData["roles"] = arrayUnion(role);
    }

    return setDoc(memberRef, memberData, { merge: true }).then(() => {
      const member = this.members.addData(user.id);
      member.units.push(...units);
      member.setup(memberData);
      if (!user.properties.includes(this)) user.properties.push(this);
    });
  }

  findUnitWithName(name: string): Promise<UnitData | undefined> {
    return new Promise((resolve) => {
      const checkUnits = () => {
        this.units.forEach((unit) => {
          if (unit.name === name) resolve(unit);
        });
        resolve(undefined);
      };
      if (this.unitsLoaded) {
        checkUnits();
      } else {
        this.loadUnits(() => {
          checkUnits();
        });
      }
    });
  }

  checkForUserRoles(user: UserData) {
    return this.members.getData(user.id)?.roles;
  }

  checkForLandlordRoles(user: UserData) {
    return this.checkForUserRoles(user)?.includes(Role.LANDLORD) ?? false;
  }

  checkForEntrepreneurRoles(user: UserData) {
    return this.checkForUserRoles(user)?.includes(Role.ENTREPRENEUR) ?? false;
  }

  checkForTenantRoles(user: UserData) {
    return this.checkForUserRoles(user)?.includes(Role.RENTER) ?? false;
  }

  deleteProperty(completion: (error?: Error) => void) {
    const db = getFirestore();
    const propertyRef = doc(db, "properties", this.id);

    updateDoc(propertyRef, {
      deletedBy: DataCreator.getDeletedBy(),
    })
      .then(() => {
        LocalData.currentUser.memberDatas = LocalData.currentUser.memberDatas.filter(
          (memberData) => memberData !== this.members.getData(LocalData.currentUser.id)
        );
        LocalData.currentUser.properties = LocalData.currentUser.properties.filter((property) => property !== this);
        this.companies.forEach((company) => {
          company.properties.filter((companyProperty) => companyProperty !== this);
        });
        this.deleted = true;
        completion();
      })
      .catch((error) => {
        completion(error);
      });
  }

  getUnitWithId(id: string): UnitData {
    let unit = this.units.find((unit) => unit.id === id);
    if (unit) return unit;

    unit = new UnitData(id, "units", this);
    unit.property = this;
    this.units.push(unit);
    LocalData.units.push(unit);
    return unit;
  }

  /**
   * @deprecated Use useModulesStatus-hook instead
   */
  moduleIsActivated(module: AppModuleID): boolean {
    if (this.deleted) return false;
    if (this.modules[module]) {
      return this.modules[module].active;
    }
    return AppModules.getModules().find((appModule) => appModule.id === module)?.opt === "out";
  }

  setupCollectionsListener(onUpdate: () => void): CollectionLoadedListener<PropertyCollectionData> | undefined {
    const roles = this.members.getData(LocalData.currentUser.id)?.roles.map((role) => role.id) ?? [];
    if (roles.length > 0) {
      return this.collections.setupRealtimeListenerWithFilter((ref) => query(ref, where("readAccess.roles", "array-contains-any", roles)), onUpdate);
    }
  }

  loadCollections(): Promise<PropertyCollectionData[]> {
    const roles = this.members.getData(LocalData.currentUser.id)?.roles.map((role) => role.id) ?? [];
    if (roles.length > 0) {
      return new Promise((resolve) =>
        this.collections.loadWithFilter(
          (ref) => query(ref, where("readAccess.roles", "array-contains-any", roles)),
          (data) => {
            this.collections.loaded = true;
            resolve(data);
          }
        )
      );
    }
    return Promise.resolve([]);
  }

  hasAccessToUnit(unitID: string): boolean {
    const member = this.members.getData(LocalData.currentUser.id);
    return (
      member?.roles.includes(Role.LANDLORD) ||
      member?.roles.includes(Role.ENTREPRENEUR) ||
      member?.units.find((unit) => unit.id === unitID) !== undefined
    );
  }
}
